import {ListView} from '@github-ui/list-view'
import {ListViewDensityToggle} from '@github-ui/list-view/ListViewDensityToggle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import type {RepositoryItem} from '@github-ui/use-repos-list-results'
import {Spinner} from '@primer/react'
import type {ComponentProps} from 'react'

import {getDisplayReposCount} from '../shared/screen-reader-message-utils'
import type {OrderOptions} from '../shared/sorting-options'
import styles from './ReposList.module.css'
import {ReposListItem} from './ReposListItem'
import {type DirectionRestrictedKeys, SortingDropdown} from './SortingDropdown'

export type ListViewVariant = ComponentProps<typeof ListView>['variant']

export function ReposList({
  query,
  showNameWithOwner = false,
  compactMode,
  onCompactModeChanged,
  showSpinner,
  repositoryCount,
  repos,
  sortingItemSelected,
  onSortingItemSelect,
  defaultSortingKey,
  sortingOptions,
  noReposMessage,
  directionRestrictedKeys,
}: {
  query?: string
  showNameWithOwner?: boolean
  compactMode: boolean
  onCompactModeChanged: (value: boolean) => void
  showSpinner: boolean
  repositoryCount: number
  repos: RepositoryItem[]
  sortingItemSelected?: string
  onSortingItemSelect: (key: string) => void
  defaultSortingKey: string
  sortingOptions: Record<string, OrderOptions>
  noReposMessage: React.ReactElement
  directionRestrictedKeys?: DirectionRestrictedKeys
}) {
  const metadata = (
    <ListViewMetadata
      title={<span className={styles.ReposCountText}>{getDisplayReposCount(repositoryCount)}</span>}
      densityToggle={<ListViewDensityToggle />}
    >
      {showSpinner && <Spinner size="small" />}
      <SortingDropdown
        sortingItemSelected={sortingItemSelected || ''}
        onSortingItemSelect={onSortingItemSelect}
        defaultSortingKey={defaultSortingKey}
        sortingOptions={sortingOptions}
        directionRestrictedKeys={directionRestrictedKeys}
      />
    </ListViewMetadata>
  )

  return (
    <div className={styles.ReposListContainer} data-listview-repos-list>
      <ListView
        onVariantChange={value => onCompactModeChanged(value === 'compact')}
        variant={compactMode ? 'compact' : 'default'}
        metadata={metadata}
        title="Repositories list"
        titleHeaderTag="h2"
      >
        {repos.length > 0 ? (
          repos.map(repo => (
            <ReposListItem
              key={`${repo.owner}/${repo.name}`}
              repo={repo}
              showNameWithOwner={showNameWithOwner}
              query={query}
            />
          ))
        ) : (
          // A ListView is a <ul> element, so it only accepts <li> children. This component is a workaround to allow
          // Blankslate to be used as a child of a ListView. We don't need to add any additional styles to the <li> element
          // because the ListView already ensures that the <li> element is styled correctly.
          <li>{noReposMessage}</li>
        )}
      </ListView>
    </div>
  )
}

try{ ReposList.displayName ||= 'ReposList' } catch {}